import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Sparkles, Compass, Eye, MessageCircleHeart, CheckCircle2, Target, Lightbulb, Mail, Check, History, Home, UserPlus, LogIn, BookmarkCheck } from "lucide-react";
import ParentGate from "@/components/ParentGate";
import GuardianHistory from "@/components/GuardianHistory";
import FeedbackForm from "@/components/FeedbackForm";
import { ReportEmailForm } from "@/components/ReportEmailForm";
import { PracticeReportV2 } from "@/components/PracticeReportV2";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const Line = ({ label, icon, text, testId }) =>
  text ? (
    <div className="mb-4" data-testid={testId}>
      <div className="mb-1.5 flex items-center gap-2 text-white/70">
        <span style={{ color: ACCENT }}>{icon}</span>
        <span className="text-xs font-semibold uppercase tracking-[0.18em]">{label}</span>
      </div>
      <p className="text-base leading-relaxed text-white/85">{text}</p>
    </div>
  ) : null;

const Section = ({ icon, title, items, testId }) => {
  if (!items || items.length === 0) return null;
  return (
    <motion.div variants={item} className="mt-9" data-testid={testId}>
      <div className="mb-3 flex items-center gap-2.5 text-white/80">
        <span style={{ color: ACCENT }}>{icon}</span>
        <h3 className="text-sm font-semibold tracking-wide">{title}</h3>
      </div>
      <div className="space-y-3">
        {items.map((t, i) => (
          <div key={i} className="flex items-start gap-3">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: ACCENT }} />
            <p className="text-base leading-relaxed text-white/70">{t}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// Report content schema dispatch: v1 (Simulation #1) = existing renderer below, unchanged.
// v2 (Simulation #2) = dedicated renderer; Phase B ships only its scaffold.
export const PracticeReport = (props) => {
  if ((props.summary?.content_schema_version || 1) === 2) return <PracticeReportV2 {...props} />;
  return <PracticeReportV1 {...props} />;
};

const PracticeReportV1 = ({ summary, simTitle, onBack, onRestart, onHome, simId, sessionId, onEmailCaptured, prefillEmail }) => {
  const [view, setView] = useState("report"); // report | gate | history
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { account } = useAuth();
  const navigate = useNavigate();
  const loading = !summary;
  const reportId = summary?.report_id;
  const child = summary?.child || {};
  const parent = summary?.parent || {};
  const caps = summary?.capabilities || [];

  // Phase 5B — proactively mint a short-lived, single-use claim capability for THIS one
  // completed attempt, stashed in sessionStorage so it survives the sign-in / Google
  // OAuth transition. Only for anonymous users; signed-in attempts are already owned.
  const claimIssued = useRef(false);
  useEffect(() => {
    if (loading || account || !sessionId || !reportId || claimIssued.current) return;
    claimIssued.current = true;
    (async () => {
      try {
        const r = await fetch(`${API}/simulation/claim-token`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (r.ok) {
          const d = await r.json();
          if (d.claimable && d.claim_token) sessionStorage.setItem("adaptience_claim", d.claim_token);
        }
      } catch { /* claim is optional; never blocks the report */ }
    })();
  }, [loading, account, sessionId, reportId]);

  // report_viewed exactly once per mount (no rerender inflation).
  const viewedRef = useRef(false);
  useEffect(() => {
    if (!loading && !viewedRef.current) {
      viewedRef.current = true;
      track("report_viewed", null, { simulation_id: simId, session_id: sessionId, report_id: reportId });
    }
  }, [loading, simId, sessionId, reportId]);

  const onReportSent = (to) => {
    setEmail(to);
    setSent(true);
    if (onEmailCaptured) onEmailCaptured(to);
  };

  if (view === "gate") return (
    <div className="relative min-h-[100dvh] w-full overflow-y-auto bg-[#0b0e12] grain" data-testid="practice-report-screen">
      <div className="relative z-10 mx-auto w-full max-w-2xl px-6 py-14 sm:px-10 sm:py-20">
        <ParentGate onPass={() => setView("history")} onCancel={() => setView("report")} />
      </div>
    </div>
  );
  if (view === "history") return (
    <div className="relative min-h-[100dvh] w-full overflow-y-auto bg-[#0b0e12] grain" data-testid="practice-report-screen">
      <div className="relative z-10 mx-auto w-full max-w-2xl px-6 py-14 sm:px-10 sm:py-20">
        <GuardianHistory onBack={() => setView("report")} />
      </div>
    </div>
  );

  return (
    <div className="relative min-h-[100dvh] w-full overflow-y-auto bg-[#0b0e12] grain" data-testid="practice-report-screen">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -left-24 top-0 h-[50vh] w-[50vh] rounded-full blur-[130px]" style={{ background: "rgba(79,163,217,0.10)" }} />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-2xl px-6 py-14 sm:px-10 sm:py-20">
        <div className="mb-10 flex items-center gap-3">
          <button onClick={onBack} data-testid="report-back-btn" className="inline-flex items-center gap-2 text-sm text-white/55 transition-colors hover:text-white/90">
            <ArrowLeft size={16} /> Back
          </button>
          <span className="text-white/20">·</span>
          <button onClick={onHome} data-testid="home-btn" className="inline-flex items-center gap-2 text-sm text-white/55 transition-colors hover:text-white/90">
            <Home size={15} /> Home
          </button>
        </div>

        <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-white/50">Adaptience&trade; Practice Report</span>
        <h1 className="mb-3 mt-2 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">{simTitle || "Practice Report"}</h1>

        {loading ? (
          <div className="mt-10 flex items-center gap-3 text-white/60" data-testid="report-loading">
            <Loader2 size={20} className="animate-spin" style={{ color: ACCENT }} /> Preparing your Practice Report…
          </div>
        ) : (
          <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.08 } } }} data-testid="practice-report-content">
            <motion.p variants={item} className="mt-3 text-sm leading-relaxed text-white/50">
              A reflection from this practice experience — not a score, diagnosis, or personality assessment.
            </motion.p>

            <motion.div variants={item} className="mt-9" data-testid="report-reflection">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: ACCENT }}>Your Reflection</h3>
              <Line label="What You Did Well" icon={<CheckCircle2 size={15} />} text={child.did_well} testId="report-did-well" />
              <Line label="Something to Practise" icon={<Target size={15} />} text={child.practise} testId="report-practise" />
              <Line label="Try This Next Time" icon={<Lightbulb size={15} />} text={child.try_next} testId="report-try-next" />
            </motion.div>

            {caps.length > 0 && (
              <motion.div variants={item} className="mt-9" data-testid="report-capabilities">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: ACCENT }}>Capabilities Practised</h3>
                <div className="flex flex-wrap gap-2">
                  {caps.map((c, i) => (
                    <span key={i} className="rounded-full border border-white/12 bg-white/[0.04] px-3.5 py-1.5 text-sm text-white/75" data-testid="capability-chip">{c}</span>
                  ))}
                </div>
              </motion.div>
            )}

            <motion.div variants={item} className="mt-12 border-t border-white/10 pt-8">
              <h2 className="text-base font-semibold tracking-wide text-white/85">For Parents &amp; Guardians</h2>
            </motion.div>
            <Section icon={<Sparkles size={16} />} title="Strengths Observed" items={parent.strengths} testId="report-strengths" />
            <Section icon={<Compass size={16} />} title="Worth Practising" items={parent.worth_practising} testId="report-worth" />
            <Section icon={<Eye size={16} />} title="What We Noticed" items={parent.what_we_noticed} testId="report-noticed" />
            <Section icon={<MessageCircleHeart size={16} />} title="Try This Together" items={parent.try_this_together} testId="report-together" />

            {/* KEEP YOUR PRACTICE REPORT — never gate the insight, only the convenience of keeping it */}
            <motion.div variants={item} className="mt-12 rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm sm:p-8" data-testid="keep-report">
              <div className="mb-2 flex items-center gap-2.5" style={{ color: ACCENT }}>
                <Mail size={16} />
                <span className="text-xs font-semibold uppercase tracking-[0.2em]">Keep Your Practice Report</span>
              </div>
              {sent ? (
                <div className="mt-3 flex items-center gap-3 text-white/85" data-testid="report-email-sent">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: ACCENT }}><Check size={18} className="text-white" /></span>
                  <p className="text-base">Sent. Check your inbox for your Practice Report.</p>
                </div>
              ) : (
                <ReportEmailForm reportId={reportId} onSent={onReportSent} />
              )}
            </motion.div>

            {/* PHASE 5B — optional account CTA. Never gates report/email/feedback/Practice Again. */}
            {!account ? (
              <motion.div variants={item} className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm sm:p-8" data-testid="claim-cta">
                <div className="mb-2 flex items-center gap-2.5" style={{ color: ACCENT }}>
                  <BookmarkCheck size={16} />
                  <span className="text-xs font-semibold uppercase tracking-[0.2em]">Keep this practice in My Adaptience</span>
                </div>
                <p className="mt-2 text-base leading-relaxed text-white/60">
                  Sign in or create an account to add this experience to your Practice History.
                </p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={() => navigate("/signin")}
                    data-testid="claim-create-account-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-opacity hover:opacity-90"
                    style={{ background: ACCENT }}
                  >
                    <UserPlus size={16} /> Create account
                  </button>
                  <button
                    onClick={() => navigate("/signin")}
                    data-testid="claim-signin-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white/85 transition-colors hover:bg-white/5"
                  >
                    <LogIn size={16} /> Sign in
                  </button>
                </div>
                <p className="mt-3 text-xs text-white/40">Optional — your report, email and feedback all work without an account.</p>
              </motion.div>
            ) : (
              <motion.div variants={item} className="mt-8 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4" data-testid="claim-saved">
                <span className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: ACCENT }}><BookmarkCheck size={16} className="text-white" /></span>
                <p className="text-sm text-white/80">Saved to My Adaptience.</p>
              </motion.div>
            )}

            <FeedbackForm sessionId={sessionId} prefillEmail={sent ? email.trim() : prefillEmail} />

            <motion.button
              variants={item}
              onClick={onRestart}
              data-testid="report-restart-btn"
              className="mt-10 inline-flex items-center gap-3 rounded-full border px-7 py-3.5 text-sm font-medium tracking-wide transition-[color,background-color] duration-500"
              style={{ borderColor: ACCENT, color: ACCENT }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = ACCENT; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = ACCENT; }}
            >
              Practice again
            </motion.button>

            <motion.div variants={item} className="mt-6">
              <button onClick={() => setView("gate")} data-testid="open-history-btn" className="inline-flex items-center gap-2 text-sm text-white/50 transition-colors hover:text-white/85">
                <History size={15} /> View Guardian History (for grown-ups)
              </button>
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PracticeReport;
